import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export type UserRole = "admin" | "supplier" | "buyer" | "support";

interface UserRolesData {
  roles: UserRole[];
  userId: string | null;
}

const ROLES_TIMEOUT_MS = 8000;

/** Race a promise against a timeout - prevents auth from hanging when Supabase is slow (e.g. disk IO throttling) */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

/**
 * Centralized user roles query with React Query caching
 * This prevents duplicate API calls and improves performance
 *
 * PERFORMANCE: Optimized for fast initial load with caching
 */
export function useUserRolesQuery(userId: string | null) {
  return useQuery<UserRolesData>({
    queryKey: ['user_roles', userId],
    queryFn: async () => {
      if (!userId) {
        return { roles: [], userId: null };
      }

      const startTime = performance.now();

      const { data, error } = await withTimeout(
        supabase.from('user_roles').select('role').eq('user_id', userId),
        ROLES_TIMEOUT_MS,
        'Roles fetch'
      );

      if (error) {
        logger.error('Error fetching user roles', {
          error: error.message,
          code: error.code,
          userId,
          hint: 'Check: user_roles table has row for this user_id, RLS policy allows authenticated users to read own roles',
        });
        throw error;
      }

      // Log timing in development for performance monitoring
      if (import.meta.env.DEV) {
        const elapsed = performance.now() - startTime;
        if (elapsed > 300) {
          logger.warn('Roles fetch slow', { elapsed: `${elapsed.toFixed(0)}ms` });
        }
      }

      const roles = (data?.map(r => r.role) || []) as UserRole[];

      return {
        roles,
        userId,
      };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Only refetch if stale, preventing excessive calls on remounts
    // PERFORMANCE: Keep previous data while refetching
    placeholderData: (previousData) => previousData,
    // PERFORMANCE: Quick retry for fast failure recovery
    retry: 1,
    retryDelay: 300,
  });
}
